import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ReactComponent as EditIcon } from '../../assets/img/icons/edit.svg'
import { ReactComponent as RemoveIcon } from '../../assets/img/icons/remove.svg'
import { openModalWindow } from '../../store/ModalWindowData/action'
import { getScheduledNotificationsRequest } from '../../store/ScheduledNotifications/action'
import { ApplicationState } from '../../store'
import greenHills from '../../assets/img/greenHills.svg'
import '../../assets/scss/layouts/greenHills.scss'
import { ScheduledNotificationDto, ScheduledNotificationStatus } from '../../sdk'
import { describeSchedule, formatSwedishDateTime } from '../../utils/scheduledNotifications'

const STATUS_LABELS: Record<ScheduledNotificationStatus, string> = {
  [ScheduledNotificationStatus.ACTIVE]: 'Scheduled',
  [ScheduledNotificationStatus.COMPLETED]: 'Sent',
}

const describeLastSend = (notification: ScheduledNotificationDto): string =>
  notification.lastSentAt
    ? `${formatSwedishDateTime(notification.lastSentAt)} (${notification.lastRecipientCount ?? 0} patients)`
    : '—'

const NotificationsPage: React.FC = () => {
  const dispatch = useDispatch()
  const notifications = useSelector((state: ApplicationState) => state.scheduledNotifications.all)
  const loading = useSelector((state: ApplicationState) => state.scheduledNotifications.loading)
  const errorMsg = useSelector((state: ApplicationState) => state.scheduledNotifications.errorMsg)

  useEffect(() => {
    dispatch(getScheduledNotificationsRequest())
  }, [])

  const openCreate = () => dispatch(openModalWindow('createScheduledNotification', { notification: null }))

  const openEdit = (notification: ScheduledNotificationDto) =>
    dispatch(openModalWindow('editScheduledNotification', { notification }))

  const openDelete = (notification: ScheduledNotificationDto) =>
    dispatch(openModalWindow('deleteScheduledNotification', { id: notification.id, title: notification.title }))

  return (
    <div>
      <div className="card-box patient-info">
        <div
          className="box-title"
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 24px 16px' }}
        >
          <h3 style={{ margin: 0 }}>Notifications</h3>
          <button className="g_btn g_btn--primary" type="button" onClick={openCreate}>
            Create notification
          </button>
        </div>

        <div style={{ padding: '0 24px 16px', color: '#888' }}>
          Push notifications to all patients who have notifications turned on. Times are Swedish time.
        </div>

        {errorMsg && (
          <div className="login_form-error" style={{ padding: '0 24px 16px' }}>
            {errorMsg}
          </div>
        )}

        {loading && <div style={{ padding: '16px 24px' }}>Loading...</div>}

        {!loading && notifications.length === 0 && (
          <div style={{ padding: '24px', color: '#888' }}>No notifications yet. Create your first one.</div>
        )}

        {notifications.length > 0 && (
          <div>
            <div className="voucher-code" style={{ fontWeight: 600, borderBottom: '1px solid #eee' }}>
              <span className="statistic-item" style={{ flex: 3 }}>Notification</span>
              <span className="statistic-item" style={{ flex: 2 }}>Schedule</span>
              <span className="statistic-item" style={{ flex: 2 }}>Next send</span>
              <span className="statistic-item" style={{ flex: 2 }}>Last sent</span>
              <span className="statistic-item" style={{ flex: 1 }}>Status</span>
              <span className="statistic-item" style={{ flex: 1 }}></span>
            </div>
            {notifications.map((notification: ScheduledNotificationDto) => (
              <div
                key={notification.id}
                className="voucher-code"
                style={{ borderBottom: '1px solid #f5f5f5', alignItems: 'center' }}
              >
                <span className="statistic-item" style={{ flex: 3 }}>
                  <span style={{ display: 'block' }}>{notification.title}</span>
                  <span style={{ display: 'block', color: '#888', fontSize: '0.9em' }}>{notification.body}</span>
                </span>
                <span className="statistic-item" style={{ flex: 2 }}>{describeSchedule(notification.schedule)}</span>
                <span className="statistic-item" style={{ flex: 2 }}>
                  {formatSwedishDateTime(notification.nextSendAt)}
                </span>
                <span className="statistic-item" style={{ flex: 2 }}>{describeLastSend(notification)}</span>
                <span className="statistic-item" style={{ flex: 1 }}>{STATUS_LABELS[notification.status]}</span>
                <span className="statistic-item" style={{ flex: 1, display: 'flex', gap: '12px', alignItems: 'center' }}>
                  {notification.status === ScheduledNotificationStatus.ACTIVE && (
                    <EditIcon
                      className="action-btn-svg"
                      style={{ cursor: 'pointer' }}
                      onClick={() => openEdit(notification)}
                    />
                  )}
                  <RemoveIcon
                    className="action-btn-svg"
                    style={{ fill: '#BA1717', cursor: 'pointer' }}
                    onClick={() => openDelete(notification)}
                  />
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      <img className="greenHills" src={greenHills} alt="" />
    </div>
  )
}

export default NotificationsPage
